import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { unwrap } from '../utils/api';

const LANGUAGES = ['Yiddish', 'English', 'Hebrew'];

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [variants, setVariants] = useState([]);
  const [presets, setPresets] = useState([]);
  const [addingLanguage, setAddingLanguage] = useState(null); // 'English' | 'Hebrew' | …
  const [presetForNewVariant, setPresetForNewVariant] = useState('');
  const [variantMsg, setVariantMsg] = useState('');
  const [msg, setMsg] = useState('');

  async function loadVariants() {
    const r = await api.get(`/products/GetProductVariants/${id}`);
    setVariants(unwrap(r) || []);
  }

  useEffect(() => {
    api.get(`/products/GetProductAsAdmin/${id}`).then(r => setProduct(unwrap(r)));
    api.get('/categories/GetCategoriesAndSubCategoriesSimple').then(r => setCategories(unwrap(r) || []));
    api.get('/miscellaneous/fieldLayoutPresets').then(r => setPresets(unwrap(r) || []));
    loadVariants();
  }, [id]);

  function update(k, v) { setProduct(p => ({ ...p, [k]: v })); }

  async function save() {
    const payload = { ...product, categoriesIds: (product.categoriesIds || []).map(c => c._id || c) };
    await api.post('/products/UpdateProduct', payload);
    setMsg('Saved.');
    setTimeout(() => setMsg(''), 2000);
  }

  async function createVariant() {
    if (!addingLanguage) return;
    setVariantMsg('');
    try {
      const r = await api.post('/products/CreateLanguageVariant', {
        sourceProductId: id,
        language: addingLanguage,
        presetId: presetForNewVariant || undefined,
      });
      const variant = unwrap(r);
      // Jump straight to the new variant's field editor so the admin/editor
      // can fine-tune the layout for that language.
      navigate(`/pages/editProductFields/${variant._id}`);
    } catch (e) {
      const m = e?.response?.data?.body;
      setVariantMsg(typeof m === 'string' ? m : 'Could not create variant.');
    }
  }

  if (!product) return <p>Loading…</p>;

  const existingLanguages = new Set(variants.map(v => v.language));
  const missingLanguages = LANGUAGES.filter(l => !existingLanguages.has(l));

  return (
    <>
      <h1>Edit Product · {product.name}</h1>
      <div className="card" style={{ maxWidth: 720 }}>
        <div className="prop"><label>Name</label><input value={product.name || ''} onChange={e => update('name', e.target.value)} /></div>
        <div className="prop"><label>Price</label><input type="number" step="0.01" value={product.price} onChange={e => update('price', Number(e.target.value))} /></div>
        <div className="prop"><label>Tags (comma-separated)</label>
          <input value={(product.tags || []).join(', ')} onChange={e => update('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
        </div>
        <div className="prop"><label>Categories</label>
          <select multiple value={(product.categoriesIds || []).map(c => c._id || c)} onChange={e => update('categoriesIds', Array.from(e.target.selectedOptions, o => o.value))} style={{ height: 120 }}>
            {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
        <div className="prop"><label><input type="checkbox" checked={!!product.published} onChange={e => update('published', e.target.checked)} /> Published</label></div>

        {/* Color-theme prototype — when enabled, the customer editor and the
            post-payment download both show four color swatches (Original,
            Blue, Pink, Green). The CSS hue-rotate filter applied per swatch
            is computed from the (target − base) hue, so the destination
            colour always matches the swatch label regardless of the master
            image's dominant hue. Leave the checkbox off and no swatches
            show for this template. */}
        <div className="prop" style={{ borderTop: '1px solid #eef2f6', paddingTop: 12, marginTop: 6 }}>
          <label>
            <input
              type="checkbox"
              checked={!!product.themeBaseColor}
              onChange={e => update('themeBaseColor', e.target.checked ? (product.themeBaseColor || '#a8853f') : null)}
            />
            &nbsp;Allow customer to switch color themes
          </label>
        </div>
        {product.themeBaseColor && (
          <div className="prop" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ margin: 0 }}>Template base color</label>
            <input
              type="color"
              value={product.themeBaseColor || '#a8853f'}
              onChange={e => update('themeBaseColor', e.target.value)}
              style={{ width: 50, height: 36, padding: 0, border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' }}
            />
            <input
              type="text"
              value={product.themeBaseColor || ''}
              onChange={e => update('themeBaseColor', e.target.value)}
              placeholder="#a8853f"
              style={{ width: 110, fontFamily: 'monospace' }}
            />
            <span style={{ color: '#666', fontSize: 12, maxWidth: 360 }}>
              Pick the dominant decorative color of the master image. The
              Blue/Pink/Green swatches will rotate FROM this color.
            </span>
          </div>
        )}

        {/* Language variants — one template image, up to three field layouts
            (Yiddish / English / Hebrew). Editor adds a new variant by picking
            a language and (optionally) a preset to seed the field layout. */}
        <div className="prop" style={{ borderTop: '1px solid #eef2f6', paddingTop: 12, marginTop: 6 }}>
          <label style={{ fontWeight: 600 }}>Language variants</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#555' }}>This product's language:</span>
            <select
              value={product.language || 'Yiddish'}
              onChange={e => update('language', e.target.value)}
              style={{ padding: '4px 8px' }}
            >
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <p style={{ fontSize: 12, color: '#666', margin: '0 0 8px' }}>
            All variants share the same template image — only the field layout differs.
            Each group can have at most one product per language.
          </p>
          {variants.length > 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {variants.map(v => (
                v._id === id
                  ? <span key={v._id} className="btn small" style={{ background: '#e7f3ff', color: '#1990C7', cursor: 'default' }}>{v.language} (this one)</span>
                  : <Link key={v._id} className="btn small secondary" to={`/pages/editProduct/${v._id}`}>{v.language}</Link>
              ))}
            </div>
          )}
          {missingLanguages.length > 0 && (
            <>
              {addingLanguage ? (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Add a {addingLanguage} variant</div>
                  <div className="prop">
                    <label>Start from a saved layout preset (optional)</label>
                    <select value={presetForNewVariant} onChange={e => setPresetForNewVariant(e.target.value)}>
                      <option value="">— No preset (start with no fields) —</option>
                      {presets.map(p => <option key={p._id} value={p._id}>{p.name} ({(p.fields || []).length} fields)</option>)}
                    </select>
                    <p style={{ fontSize: 12, color: '#666', margin: '4px 0 0' }}>
                      Or skip the preset and lay out fields manually in the field editor.
                    </p>
                  </div>
                  <div className="row">
                    <button className="btn" onClick={createVariant}>Create {addingLanguage} variant</button>
                    <button className="btn secondary" onClick={() => { setAddingLanguage(null); setPresetForNewVariant(''); setVariantMsg(''); }}>Cancel</button>
                    {variantMsg && <span style={{ color: '#b42318', fontSize: 13 }}>{variantMsg}</span>}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {missingLanguages.map(lang => (
                    <button key={lang} type="button" className="btn small" onClick={() => setAddingLanguage(lang)}>
                      + Add {lang} variant
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Off by default. When on, the customer editor renders a switcher
            so customers can toggle between language variants of this template.
            The flag is synced across every variant on save, so it doesn't
            depend on which variant the editor opens. */}
        <div className="prop">
          <label>
            <input
              type="checkbox"
              checked={!!product.showLanguageSwitch}
              onChange={e => update('showLanguageSwitch', e.target.checked)}
            />
            &nbsp;Show language switcher to customers
          </label>
          <p style={{ fontSize: 12, color: '#666', margin: '4px 0 0' }}>
            Only takes effect once the template has 2+ language variants.
          </p>
        </div>

        <div className="row">
          <button className="btn" onClick={save}>Save</button>
          <Link className="btn secondary" to={`/pages/editProductFields/${id}`}>Edit fields</Link>
          {msg && <span style={{ color: '#198754' }}>{msg}</span>}
        </div>
      </div>
    </>
  );
}
